import { forwardRef, useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * Input component - Lucky Number Design System
 *
 * Features:
 * - Uppercase labels (design system convention)
 * - Dark and light variants for different backgrounds
 * - Error states with red border + message
 * - Success states with green checkmark
 * - Helper text support
 * - Min 44px height for touch targets
 */

// Check icon for success state
const CheckIcon = () => (
  <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// Eye icons for password toggle
const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const Input = forwardRef(function Input(
  {
    label,
    error,
    success,
    helperText,
    className,
    containerClassName,
    type = 'text',
    required,
    disabled,
    maxLength,
    showCharCount,
    addonLeft,
    addonRight,
    dark = false, // Use dark variant for light backgrounds
    onChange,
    ...props
  },
  ref
) {
  const [showPassword, setShowPassword] = useState(false);
  const [charCount, setCharCount] = useState(props.value?.length || props.defaultValue?.length || 0);

  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  const handleChange = (e) => {
    if (showCharCount || maxLength) {
      setCharCount(e.target.value.length);
    }
    onChange?.(e);
  };

  return (
    <div className={cn('w-full', containerClassName)}>
      {/* Label */}
      {label && (
        <label className={cn(
          'block mb-1.5',
          dark ? 'text-sm font-medium text-midnight' : 'text-xs font-bold uppercase tracking-wider text-cream/70'
        )}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      {/* Input wrapper */}
      <div className="relative">
        {/* Left addon */}
        {addonLeft && (
          <div className={cn(
            'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none',
            dark ? 'text-midnight/50' : 'text-cream/50'
          )}>
            {addonLeft}
          </div>
        )}

        {/* Input */}
        <input
          ref={ref}
          type={inputType}
          disabled={disabled}
          maxLength={maxLength}
          className={cn(
            // Base styles
            'w-full px-4 py-3 min-h-[44px]',
            'border rounded-md',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            // Light input (on dark backgrounds)
            !dark && [
              'bg-midnight-light text-cream placeholder:text-cream/40',
              'border-midnight-lighter',
              !error && !success && 'focus:border-petrol focus:ring-petrol/30',
            ],
            // Dark input (on light backgrounds - white bg per design spec)
            dark && [
              'bg-white text-midnight placeholder:text-midnight/50',
              'border border-midnight/30',
              !error && !success && 'focus:border-primary focus:ring-primary/30',
            ],
            // Error state
            error && 'border-error focus:border-error focus:ring-error/20',
            // Success state
            success && 'border-success focus:border-success focus:ring-success/20',
            // Disabled state
            disabled && 'opacity-50 cursor-not-allowed',
            // Addon padding
            addonLeft && 'pl-10',
            (addonRight || isPassword || success) && 'pr-10',
            className
          )}
          onChange={handleChange}
          {...props}
        />

        {/* Right side icons/addons */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-2">
          {/* Success checkmark */}
          {success && !isPassword && <CheckIcon />}

          {/* Password toggle */}
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={cn(
                'focus:outline-none transition-colors',
                dark ? 'text-midnight/50 hover:text-midnight/70' : 'text-cream/50 hover:text-cream/70'
              )}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          )}

          {/* Right addon */}
          {addonRight && !isPassword && (
            <span className={dark ? 'text-midnight/50' : 'text-cream/50'}>{addonRight}</span>
          )}
        </div>
      </div>

      {/* Helper text / Error / Character count */}
      <div className="mt-1.5 flex justify-between gap-2">
        <div className="flex-1">
          {error && (
            <p className="text-sm text-error">{error}</p>
          )}
          {!error && helperText && (
            <p className={cn('text-sm', dark ? 'text-midnight/60' : 'text-cream/50')}>{helperText}</p>
          )}
          {!error && success && typeof success === 'string' && (
            <p className="text-sm text-success">{success}</p>
          )}
        </div>
        {(showCharCount || maxLength) && (
          <p className={cn(
            'text-sm',
            maxLength && charCount >= maxLength ? 'text-error' : (dark ? 'text-midnight/50' : 'text-cream/40')
          )}>
            {charCount}{maxLength && `/${maxLength}`}
          </p>
        )}
      </div>
    </div>
  );
});

export default Input;

/**
 * Textarea component
 */
export const Textarea = forwardRef(function Textarea(
  {
    label,
    error,
    success,
    helperText,
    className,
    containerClassName,
    required,
    disabled,
    maxLength,
    showCharCount = true,
    rows = 4,
    dark = false,
    onChange,
    value,
    ...props
  },
  ref
) {
  // Track char count internally - always update on change for react-hook-form compatibility
  const [internalCount, setInternalCount] = useState(props.defaultValue?.length || 0);
  const charCount = value !== undefined ? value.length : internalCount;

  const handleChange = (e) => {
    setInternalCount(e.target.value.length);
    onChange?.(e);
  };

  return (
    <div className={cn('w-full', containerClassName)}>
      {/* Label */}
      {label && (
        <label className={cn(
          'block mb-1.5',
          dark ? 'text-sm font-medium text-midnight' : 'text-xs font-bold uppercase tracking-wider text-cream/70'
        )}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      {/* Textarea */}
      <textarea
        ref={ref}
        rows={rows}
        disabled={disabled}
        maxLength={maxLength}
        className={cn(
          // Base styles
          'w-full px-4 py-3',
          'border rounded-md',
          'transition-all duration-200 resize-none',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          // Light textarea (on dark backgrounds)
          !dark && [
            'bg-midnight-light text-cream placeholder:text-cream/40',
            'border-midnight-lighter',
            !error && !success && 'focus:border-petrol focus:ring-petrol/30',
          ],
          // Dark textarea (on light backgrounds - white bg per design spec)
          dark && [
            'bg-white text-midnight placeholder:text-midnight/50',
            'border border-midnight/30',
            !error && !success && 'focus:border-primary focus:ring-primary/30',
          ],
          // Error state
          error && 'border-error focus:border-error focus:ring-error/20',
          // Success state
          success && 'border-success focus:border-success focus:ring-success/20',
          // Disabled state
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        onChange={handleChange}
        {...props}
      />

      {/* Helper text / Error / Character count */}
      <div className="mt-1.5 flex justify-between gap-2">
        <div className="flex-1">
          {error && <p className="text-sm text-error">{error}</p>}
          {!error && helperText && (
            <p className={cn('text-sm', dark ? 'text-midnight/60' : 'text-cream/50')}>{helperText}</p>
          )}
        </div>
        {(showCharCount || maxLength) && (
          <p className={cn(
            'text-sm',
            maxLength && charCount >= maxLength ? 'text-error' : (dark ? 'text-midnight/50' : 'text-cream/40')
          )}>
            {charCount}{maxLength && `/${maxLength}`}
          </p>
        )}
      </div>
    </div>
  );
});

/**
 * Select component - Custom styled dropdown
 */
export function Select({
  label,
  error,
  helperText,
  className,
  containerClassName,
  required,
  disabled,
  placeholder = 'Select an option',
  options = [],
  value,
  onChange,
  dark = false,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Find selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption?.label || placeholder;

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && isOpen) {
      e.preventDefault();
      const currentIndex = options.findIndex(opt => opt.value === value);
      const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      if (!options[nextIndex].disabled) {
        onChange?.({ target: { value: options[nextIndex].value } });
      }
    } else if (e.key === 'ArrowUp' && isOpen) {
      e.preventDefault();
      const currentIndex = options.findIndex(opt => opt.value === value);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      if (!options[prevIndex].disabled) {
        onChange?.({ target: { value: options[prevIndex].value } });
      }
    }
  };

  const handleSelect = (optionValue) => {
    onChange?.({ target: { value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div className={cn('w-full', containerClassName)} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className={cn(
          'block mb-1.5',
          dark ? 'text-sm font-medium text-midnight' : 'text-xs font-bold uppercase tracking-wider text-cream/70'
        )}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      {/* Select trigger */}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className={cn(
            // Base styles
            'w-full px-4 py-3 min-h-[44px]',
            'border rounded-md',
            'transition-all duration-200',
            'cursor-pointer text-left',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            // Light select (on dark backgrounds)
            !dark && [
              'bg-midnight-light',
              'border-midnight-lighter',
              !error && 'focus:border-primary focus:ring-primary/30',
              selectedOption ? 'text-cream' : 'text-cream/50',
            ],
            // Dark select (on light backgrounds)
            dark && [
              'bg-white',
              'border border-midnight/30',
              !error && 'focus:border-primary focus:ring-primary/30',
              selectedOption ? 'text-midnight' : 'text-midnight/50',
            ],
            // Error state
            error && 'border-error focus:border-error focus:ring-error/20',
            // Disabled state
            disabled && 'opacity-50 cursor-not-allowed',
            // Right padding for chevron
            'pr-10',
            className
          )}
          {...props}
        >
          {displayText}
        </button>

        {/* Chevron icon */}
        <div className={cn(
          'absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none transition-transform duration-200',
          isOpen && 'rotate-180'
        )}>
          <svg className={cn('w-5 h-5', dark ? 'text-midnight/40' : 'text-cream/40')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Dropdown menu */}
        {isOpen && (
          <div className={cn(
            'absolute z-50 w-full mt-1 py-1',
            'border rounded-md shadow-lg',
            'max-h-60 overflow-auto',
            !dark && 'bg-midnight-light border-midnight-lighter',
            dark && 'bg-white border-midnight/20'
          )}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                disabled={option.disabled}
                onClick={() => !option.disabled && handleSelect(option.value)}
                className={cn(
                  'w-full px-4 py-2.5 text-left transition-colors',
                  !dark && [
                    'text-cream hover:bg-midnight-lighter',
                    option.value === value && 'bg-primary/20 text-primary-light',
                    option.disabled && 'text-cream/30 cursor-not-allowed hover:bg-transparent',
                  ],
                  dark && [
                    'text-midnight hover:bg-cream-dark',
                    option.value === value && 'bg-primary/10 text-primary-dark',
                    option.disabled && 'text-midnight/30 cursor-not-allowed hover:bg-transparent',
                  ]
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper text / Error */}
      {(error || helperText) && (
        <p className={cn(
          'mt-1.5 text-sm',
          error ? 'text-error' : (dark ? 'text-midnight/60' : 'text-cream/50')
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
}

/**
 * Checkbox component
 */
export const Checkbox = forwardRef(function Checkbox(
  {
    label,
    error,
    helperText,
    className,
    disabled,
    dark = false,
    ...props
  },
  ref
) {
  return (
    <div className="flex items-start gap-3">
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className={cn(
          'w-5 h-5 mt-0.5',
          'rounded border',
          'text-electric focus:ring-electric focus:ring-offset-0',
          'transition-colors duration-200',
          'cursor-pointer',
          dark ? 'border-midnight/30 bg-cream' : 'border-cream/30 bg-midnight-light',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-error',
          className
        )}
        {...props}
      />
      <div className="flex-1">
        {label && (
          <label className={cn(
            'text-sm',
            dark ? 'text-midnight' : 'text-cream',
            disabled && 'opacity-50'
          )}>
            {label}
          </label>
        )}
        {(error || helperText) && (
          <p className={cn(
            'text-sm mt-1',
            error ? 'text-error' : (dark ? 'text-midnight/60' : 'text-cream/50')
          )}>
            {error || helperText}
          </p>
        )}
      </div>
    </div>
  );
});

/**
 * Radio Group component
 */
export function RadioGroup({
  label,
  name,
  options = [],
  value,
  onChange,
  error,
  helperText,
  disabled,
  dark = false,
  className,
}) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className={cn(
          'block mb-3',
          dark ? 'text-sm font-medium text-midnight' : 'text-xs font-bold uppercase tracking-wider text-cream/70'
        )}>
          {label}
        </label>
      )}
      <div className="space-y-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex items-center gap-3 cursor-pointer',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange?.(e.target.value)}
              disabled={disabled || option.disabled}
              className={cn(
                'w-5 h-5',
                dark ? 'border-midnight/30' : 'border-cream/30',
                'text-electric focus:ring-electric focus:ring-offset-0',
                error && 'border-error'
              )}
            />
            <div>
              <span className={cn('text-sm', dark ? 'text-midnight' : 'text-cream')}>
                {option.label}
              </span>
              {option.description && (
                <p className={cn('text-xs', dark ? 'text-midnight/50' : 'text-cream/50')}>
                  {option.description}
                </p>
              )}
            </div>
          </label>
        ))}
      </div>
      {(error || helperText) && (
        <p className={cn(
          'mt-2 text-sm',
          error ? 'text-error' : (dark ? 'text-midnight/60' : 'text-cream/50')
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
}
